import { useEffect, useRef } from "react";
import { loadLockPrefs, MIN_FOREGROUND_IDLE_MS } from "../lockPrefs";

/** Delay before locking: the chosen time in the background; never under a minute of
 *  inactivity while Veto is open in front ("Right away" = 15 s, background only). */
export function autoLockDelayMs(minutes: number, hidden: boolean): number | null {
  if (!minutes || minutes <= 0) return null;
  const ms = minutes * 60_000;
  return hidden ? ms : Math.max(ms, MIN_FOREGROUND_IDLE_MS);
}

/**
 * Clears the in-memory vault key after idle / background (PWA best practice).
 * Does not wipe IndexedDB — sealed ciphertext stays; user re-enters PIN.
 */
export function useVaultAutoLock(opts: { screen: string; onLock: () => void }) {
  const onLockRef = useRef(opts.onLock);
  onLockRef.current = opts.onLock;

  useEffect(() => {
    if (opts.screen !== "app") return;

    let timer: number | undefined;
    const clear = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
    };

    const arm = (hidden: boolean) => {
      clear();
      const delay = autoLockDelayMs(loadLockPrefs().autoLockMinutes, hidden);
      if (delay === null) return;
      timer = window.setTimeout(() => onLockRef.current(), delay);
    };

    const onActivity = () => {
      if (document.visibilityState === "visible") arm(false);
    };

    const onVis = () => {
      const prefs = loadLockPrefs();
      if (document.visibilityState === "hidden") {
        if (prefs.lockOnHide) arm(true);
      } else {
        arm(false);
      }
    };

    arm(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pointerdown", onActivity);
    window.addEventListener("keydown", onActivity);
    return () => {
      clear();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pointerdown", onActivity);
      window.removeEventListener("keydown", onActivity);
    };
  }, [opts.screen]);
}
